/**
 * Tree slice.
 *
 * The OST tree itself: the four node collections, canvas positions,
 * connections, and every node mutation.
 *
 * Slices share one store: `set` and `get` are typed over the whole AppStore,
 * so an action here can read or write another slice's state where the domains
 * genuinely interact (loading a project resets the tree, converting a triage
 * item calls the tree's addNode).
 */
import type { StateCreator } from 'zustand';
import { deleteStoryIssue, pushStoryToGitHub } from '../../api/ostGithub';
import type {
    NodeBase,
    NodeConnection,
    NodeType,
    OpportunityNode,
    OstState,
    OutcomeNode,
    SolutionNode,
    StoryNode
} from '../../types';
import {
    addRelationOnce,
    appendNodeToTree,
    applyConnectionAcrossTree,
    computeNodePosition,
    getDefaultNodePanelPosition,
    randomId,
    removeNodeFromTree,
    removeRelationOnce,
    updateNodeInTree
} from '../../utils/ostUtils';
import { initialData } from '../initialData';
import { patchData, saveData } from '../persistence';
import type { AppStore, NewNodePayload } from '../storeTypes';

export const createTreeSlice: StateCreator<
    AppStore,
    [],
    [],
    Pick<
        AppStore,
        | 'solutionChildType'
        | 'setSolutionChildType'
        | 'outcome'
        | 'opportunities'
        | 'solutions'
        | 'userStories'
        | 'selectedNode'
        | 'setSelectedNode'
        | 'positions'
        | 'nodePanelPosition'
        | 'setPosition'
        | 'setNodePanelPosition'
        | 'canvasView'
        | 'setCanvasView'
        | 'connections'
        | 'addConnection'
        | 'removeConnection'
        | 'updateNodeById'
        | 'addRelation'
        | 'removeRelation'
        | 'reparentNode'
        | 'clearAll'
        | 'addNode'
        | 'updateNode'
        | 'canDeleteNode'
        | 'deleteNode'
        | 'pushToGitHub'
    >
> = (set, get) => ({
    solutionChildType: initialData.solutionChildType || 'story',

    setSolutionChildType: (t: 'story' | 'experiment') => {
        set({ solutionChildType: t });
    },


    outcome: initialData.outcome,

    opportunities: initialData.opportunities,
    solutions: initialData.solutions,
    userStories: initialData.userStories,


    selectedNode: null,

    setSelectedNode: (node: NodeBase | null) => set({ selectedNode: node }),


    positions: initialData.positions || {},

    nodePanelPosition: initialData.nodePanelPosition || getDefaultNodePanelPosition(),

    // Not part of OstState / patchData / saveData — this is ephemeral per-session UI
    // state, not OST data, so it deliberately never reaches the `.zey` export/backup
    // or the server. Reset to null (see setOst and clearAll below) whenever a
    // genuinely different tree's data is loaded, so Canvas.tsx fits it fresh.
    canvasView: null,

    setCanvasView: (view: { pan: { x: number; y: number }; zoom: number } | null) => set({ canvasView: view }),

    setPosition: (id: string, pos: { x: number; y: number }) => {
        let storePos = { ...pos };
        // Apply project-scoped grid snapping if enabled
        if (get().snapToGrid) {
            const spacing = get().gridSpacing || 15;
            storePos = {
                x: Math.round(storePos.x / spacing) * spacing,
                y: Math.round(storePos.y / spacing) * spacing,
            };
        }
        const newPositions = { ...get().positions, [id]: storePos };
        set({ positions: newPositions });
        patchData({
            positions: newPositions,
            nodePanelPosition: get().nodePanelPosition,
        });
    },

    setNodePanelPosition: (pos: { x: number; y: number }) => {
        set({ nodePanelPosition: pos });
        patchData({
            positions: get().positions,
            nodePanelPosition: pos,
        });
    },


    connections: [],


    addConnection: (connection: NodeConnection) => {
        set((state) => {
            const finalState: OstState = {
                ...applyConnectionAcrossTree(state, connection, addRelationOnce),
                positions: state.positions,
                nodePanelPosition: state.nodePanelPosition,
                solutionChildType: state.solutionChildType,
                connections: [...state.connections, connection],
            };

            patchData({ ...finalState });

            return finalState;
        });
    },

    removeConnection: (connectionId: string) => {
        set((state) => {
            const conn = state.connections.find((c) => c.id === connectionId);
            if (!conn) {
                return { ...state, connections: state.connections.filter((c) => c.id !== connectionId) };
            }

            const finalState: OstState = {
                ...applyConnectionAcrossTree(state, conn, removeRelationOnce),
                positions: state.positions,
                nodePanelPosition: state.nodePanelPosition,
                solutionChildType: state.solutionChildType,
                connections: state.connections.filter((c) => c.id !== connectionId),
            };

            patchData({ ...finalState });

            return finalState;
        });
    },


    // Helper to update node by id in child arrays and outcome
    updateNodeById: (id: string, updater: (node: NodeBase) => NodeBase) => {
        set((state) => {
            let changed = false;
            const clone = { ...state };

            if (state.outcome && state.outcome.id === id) {
                clone.outcome = updater(state.outcome) as OutcomeNode;
                changed = true;
            }

            clone.opportunities = state.opportunities.map((n) => {
                if (n.id === id) {
                    changed = true;
                    return updater(n as NodeBase) as typeof n;
                }
                return n;
            });
            clone.solutions = state.solutions.map((n) => {
                if (n.id === id) {
                    changed = true;
                    return updater(n as NodeBase) as typeof n;
                }
                return n;
            });
            clone.userStories = state.userStories.map((n) => {
                if (n.id === id) {
                    changed = true;
                    return updater(n as NodeBase) as typeof n;
                }
                return n;
            });

            if (!changed) return state;

            // saveData, not patchData: this writes the tree fields only, dropping other
            // persisted keys. Currently unobservable — useStore hydrates just seven fields
            // from this cache (see persistence.ts) and the server owns the rest — but it is
            // a replace, so it is left explicit rather than quietly turned into a merge.
            saveData({
                outcome: clone.outcome,
                opportunities: clone.opportunities,
                solutions: clone.solutions,
                userStories: clone.userStories,
                positions: clone.positions || {},
            });
            return clone;
        });
    },


    addRelation: (fromNodeId: string, toNodeId: string, relationType: string = 'related') => {
        // Source stores outgoing relation, target stores inbound relation marker
        get().updateNodeById(fromNodeId, (node) => {
            const relations = node.relations || [];
            if (relations.some((r) => r.nodeId === toNodeId && r.type === relationType && r.direction === 'outgoing')) return node;
            return {
                ...node,
                relations: [...relations, { nodeId: toNodeId, type: relationType, direction: 'outgoing' }],
            };
        });
        get().updateNodeById(toNodeId, (node) => {
            const relations = node.relations || [];
            if (relations.some((r) => r.nodeId === fromNodeId && r.type === relationType && r.direction === 'incoming')) return node;
            return {
                ...node,
                relations: [...relations, { nodeId: fromNodeId, type: relationType, direction: 'incoming' }],
            };
        });
    },


    // Moves an existing node under a new parent — just a parentId patch via the generic
    // updateNodeById updater (same pattern addRelation/removeRelation above use). The node's own
    // children keep referencing it by id regardless of its parent, so nothing else needs updating.
    // Used by the node-on-node drag-to-connect gesture (Canvas.tsx) for a "reparent" drop — a node
    // dropped one hierarchy level shallower than itself, e.g. a solution dragged onto a different
    // opportunity.
    reparentNode: (nodeId: string, newParentId: string) => {
        get().updateNodeById(nodeId, (node) => ({ ...node, parentId: newParentId }));
    },

    removeRelation: (fromNodeId: string, toNodeId: string) => {
        get().updateNodeById(fromNodeId, (node) => {
            const relations = (node.relations || []).filter((r) => !(r.nodeId === toNodeId && r.direction === 'outgoing'));
            return { ...node, relations };
        });
        get().updateNodeById(toNodeId, (node) => {
            const relations = (node.relations || []).filter((r) => !(r.nodeId === fromNodeId && r.direction === 'incoming'));
            return { ...node, relations };
        });
    },


    clearAll: () => {
        set({ outcome: null, opportunities: [], solutions: [], userStories: [], positions: {}, connections: [], canvasView: null });
        // saveData, not patchData: clearing is meant to drop everything else too.
        saveData({ outcome: null, opportunities: [], solutions: [], userStories: [], positions: {}, connections: [] });
    },


    addNode: (type: NodeType, data: NewNodePayload, parentId?: string | null, initialPosition?: { x: number; y: number } | null) => {
        const id = randomId();
        if (type === 'outcome') {
            const newOutcome: OutcomeNode = {
                id,
                title: data.title || 'New Outcome',
                description: data.description || '',
                type: 'outcome',
            };
            set((state) => {
                patchData({ outcome: newOutcome });
                return { ...state, outcome: newOutcome };
            });
            return;
        }

        const nodeBase: NodeBase = {
            id,
            type,
            title: data.title || 'Untitled',
            description: data.description || '',
            parentId: parentId || null,
            relationIds: [],
        };

        const node: OpportunityNode | SolutionNode | StoryNode =
            type === 'opportunity'
                ? ({ ...nodeBase, type: 'opportunity', moscow: data.moscow || '' } as OpportunityNode)
                : type === 'solution'
                    ? ({
                        ...nodeBase,
                        type: 'solution',
                        moscow: data.moscow || '',
                        status: 'Idea',
                    } as SolutionNode)
                    : ({
                        ...nodeBase,
                        type: type === 'story' || type === 'experiment' || type === 'enablerNode' ? type : 'story',
                        moscow: data.moscow || '',
                        status: 'Draft',
                        asA: data.asA || '',
                        iWant: data.iWant || '',
                        soThat: data.soThat || '',
                        acceptanceCriteria: data.acceptanceCriteria || '',
                        assumption: data.assumption || '',
                        method: data.method || '',
                        successMetric: data.successMetric || '',
                        validated: data.validated || '',
                        githubUrl: '',
                    } as StoryNode);

        set((state) => {
            let nextState = { ...state };

            // Delegate position calculation to the pure canvasUtils helper so layout
            // logic stays out of the state manager.
            const parentPos = parentId ? state.positions[parentId] : null;
            const pos = computeNodePosition(type, parentPos, state.positions, initialPosition);

            nextState = { ...nextState, ...appendNodeToTree(state, node) };

            const nextPositions = { ...state.positions, [node.id]: pos };
            nextState.positions = nextPositions;

            // saveData, not patchData: this writes the tree fields only, dropping other
            // persisted keys. Currently unobservable — useStore hydrates just seven fields
            // from this cache (see persistence.ts) and the server owns the rest — but it is
            // a replace, so it is left explicit rather than quietly turned into a merge.
            saveData({
                outcome: nextState.outcome,
                opportunities: nextState.opportunities,
                solutions: nextState.solutions,
                userStories: nextState.userStories,
                positions: nextPositions,
            });

            return nextState;
        });
    },


    updateNode: (type: NodeType, nodeId: string, data: Partial<NodeBase>) => {
        set((state) => {
            const nextState = { ...state, ...updateNodeInTree(state, type, nodeId, data) };

            // saveData, not patchData: this writes the tree fields only, dropping other
            // persisted keys. Currently unobservable — useStore hydrates just seven fields
            // from this cache (see persistence.ts) and the server owns the rest — but it is
            // a replace, so it is left explicit rather than quietly turned into a merge.
            saveData({
                outcome: nextState.outcome,
                opportunities: nextState.opportunities,
                solutions: nextState.solutions,
                userStories: nextState.userStories,
                positions: nextState.positions || {},
            });

            return nextState;
        });
    },


    canDeleteNode: (type: NodeType, nodeId: string) => {
        const state = get();
        const childCheck: Record<NodeType, NodeBase[]> = {
            outcome: state.opportunities,
            // An opportunity's children can be solutions *and* sub-opportunities — the latter via
            // AI slicing (SlicePanel targeting an opportunity) or the node-on-node drag-to-connect
            // gesture's reparent (Canvas.tsx / reparentNode above) — so both lists must be clear.
            opportunity: [...state.solutions, ...state.opportunities],
            solution: state.userStories,
            story: [],
            experiment: [],
            enablerNode: [],
        };

        const list = childCheck[type] || [];
        return !list.some((child) => child.parentId === nodeId);
    },


    deleteNode: async (type: NodeType, nodeId: string) => {
        const state = get();
        // A failed GitHub cleanup must not block deleting the node locally, so the
        // error is captured and returned for the caller to surface rather than thrown.
        let githubError: string | undefined;

        if (type === 'story' || type === 'experiment') {
            const story = state.userStories.find((o) => o.id === nodeId);
            if (story?.githubUrl) {
                try {
                    await deleteStoryIssue(story, state.githubToken, state.githubRepo);
                } catch (err) {
                    githubError = `Node deleted, but its GitHub issue could not be removed: ${(err as Error).message}`;
                }
            }
        }

        set((state) => {
            const nextState = { ...state, ...removeNodeFromTree(state, type, nodeId) };

            // Remove any persisted soft-connections that referenced the deleted node
            const nextConnections = (state.connections || []).filter((c) => c.fromNodeId !== nodeId && c.toNodeId !== nodeId);

            // Helper to strip relations pointing to the deleted node from a node's relations array
            const stripRelation = (node: NodeBase | null): NodeBase | null => {
                if (!node) return null;
                const relations = (node.relations || []).filter((r) => r.nodeId !== nodeId);
                return { ...node, relations };
            };

            const nextOutcome = nextState.outcome ? (stripRelation(nextState.outcome) as OutcomeNode) : null;
            const nextOpportunities = nextState.opportunities.map((n) => stripRelation(n) as typeof n);
            const nextSolutions = nextState.solutions.map((n) => stripRelation(n) as typeof n);
            const nextUserStories = nextState.userStories.map((n) => stripRelation(n) as typeof n);

            const finalState: OstState = {
                outcome: nextOutcome,
                opportunities: nextOpportunities,
                solutions: nextSolutions,
                userStories: nextUserStories,
                positions: nextState.positions || {},
                nodePanelPosition: nextState.nodePanelPosition,
                solutionChildType: nextState.solutionChildType,
                connections: nextConnections,
                // Also remove any insight link targets that pointed to the deleted node
                insightLinks: (state.insightLinks || [])
                    .map((l) => ({ ...l, targetNodeIds: l.targetNodeIds.filter((id) => id !== nodeId) }))
                    .filter((l) => l.targetNodeIds.length > 0),
            };

            // Persist updated OST state including connections and insight links
            patchData({
                ...finalState,
            });

            return finalState;
        });

        return { githubError };
    },


    pushToGitHub: async (story: StoryNode) => {
        const { githubRepo, githubProjectId } = get();
        const issue = await pushStoryToGitHub(story, undefined, githubRepo, githubProjectId);
        // Use the node's actual type when updating so experiments are handled too
        get().updateNode(story.type as NodeType, story.id, { status: 'Pushed to GitHub', githubUrl: issue.html_url });
        return issue;
    },
});
